/**
 * Centralized route constants and cross-app URL helpers.
 *
 * Route constants use clean paths WITHOUT any prefix (no /app/, no /admin/).
 * The BrowserRouter basename adds the prefix in development; in production
 * subdomains, basename is empty and the subdomain provides the context.
 *
 * Cross-app links (marketing → app, app → admin) use VITE_*_URL env vars
 * so they resolve correctly in both path-based dev and subdomain prod.
 */

// --- App Routes (no prefix — basename handles it) ---
export const APP_ROUTES = {
  LOGIN: '/login',
  SIGNUP: '/signup',
  ACCEPT_INVITATION: '/accept-invitation',
  VERIFY_EMAIL: '/verify-email',
  RESET_PASSWORD_REQUEST: '/reset-password-request',
  SET_PASSWORD: '/set-password',
  SELECT_ORGANIZATION: '/select-organization',
  WELCOME: '/welcome',
  ONBOARDING: '/onboarding',
  ONBOARDING_WIZARD: '/onboarding/wizard',
  SETUP_MFA: '/setup-mfa',
  MFA_WAS_RESET: '/mfa-was-reset',
  DASHBOARD: '/dashboard',
  ORGANIZATIONS: '/organizations',
  CREATE_ORGANIZATION: '/create-organization',
  SUBSCRIPTION_EXPIRED: '/subscription-expired',
  TASK_LIBRARY: '/task-library',
  WORKFLOWS: '/workflows',
  MARKETPLACE: '/marketplace',
  STORAGE: '/storage',
  SETTINGS_PROFILE: '/settings/profile',
  SETTINGS_ORGANIZATION: '/settings/organization',
  SETTINGS_TEAM: '/settings/team',
  SETTINGS_BILLING: '/settings/billing',
  SETTINGS_WEBHOOKS: '/settings/webhooks',
  BILLING_SUCCESS: '/billing/success',
  BILLING_CANCEL: '/billing/cancel',
  DEPLOYMENTS: '/deployments',
} as const;

// --- Admin Routes (no prefix — basename handles it) ---
export const ADMIN_ROUTES = {
  LOGIN: '/login',
  RESET_PASSWORD_REQUEST: '/reset-password-request',
  SET_PASSWORD: '/set-password',
  DASHBOARD: '/',
  USERS: '/users',
  ORGANIZATIONS: '/organizations',
  SUBSCRIPTIONS: '/subscriptions',
  INVITATIONS: '/invitations',
  ROLES: '/roles',
  MARKETPLACE: '/marketplace',
  DRIVER_PACKS: '/driver-packs',
  /** Unified log viewer (audit / system / application classes in one page). */
  LOGS_EVENTS: '/logs/events',
  LOGS_STRIPE_WEBHOOKS: '/logs/stripe-webhooks',
  /** @deprecated Kept only as redirect targets to LOGS_EVENTS; see App.tsx. */
  LOGS_APPLICATION: '/logs/application',
  LOGS_AUDIT: '/logs/audit',
  LOGS_SYSTEM: '/logs/system',
  SYSTEM_LISTS: '/system-lists',
  SETTINGS: '/settings',
  SYSTEM_INFO: '/system-info',
  PARTNERS: '/partners',
} as const;

// --- Marketing Routes ---
export const MARKETING_ROUTES = {
  HOME: '/',
  FEATURES: '/features',
  PRICING: '/pricing',
  ABOUT: '/about',
  CONTACT: '/contact',
  TERMS: '/terms',
  PRIVACY: '/privacy',
} as const;

// --- Cross-app URL helpers ---

/** URL to the user app (e.g., "/app" in dev, "https://app.coldsea.io" in prod) */
export const APP_URL = import.meta.env.VITE_APP_URL || '';

/** URL to the admin portal (e.g., "/admin" in dev, "https://admin.coldsea.io" in prod) */
export const ADMIN_URL = import.meta.env.VITE_ADMIN_URL || '';

/** URL to the marketing site (e.g., "" in dev, "https://coldsea.io" in prod) */
export const MARKETING_URL = import.meta.env.VITE_MARKETING_URL || '';

/**
 * Build a cross-app link to the user app.
 * Use for links FROM marketing or admin TO app pages.
 * Example: appUrl('/signup') → "/app/signup" (dev) or "https://app.coldsea.io/signup" (prod)
 */
export function appUrl(path: string): string {
  return `${APP_URL}${path}`;
}

/**
 * Build a cross-app link to the admin portal.
 * Example: adminUrl('/users') → "/admin/users" (dev) or "https://admin.coldsea.io/users" (prod)
 */
export function adminUrl(path: string): string {
  return `${ADMIN_URL}${path}`;
}

/**
 * Build a cross-app link to the marketing site.
 * Example: marketingUrl('/pricing') → "/pricing" (dev) or "https://coldsea.io/pricing" (prod)
 */
export function marketingUrl(path: string): string {
  return `${MARKETING_URL}${path}`;
}

/** Settings billing URL with intent query param. */
export function billingWithIntent(intent: string): string {
  return `${APP_ROUTES.SETTINGS_BILLING}?intent=${intent}`;
}
